import type { ApiResponse } from "@/types/api/common";
import type {
	CreateStaffTicketRequest,
	SupportAssignee,
	SupportSettings,
	SupportSummary,
	SupportTicketDetail,
	SupportTicketListParams,
	SupportTicketListResult,
	SupportTicketStatus,
	SupportTicketSummary,
	UpdateSupportTicketRequest,
} from "@/types/api/support";
import { apiFetch } from "./api-client";

function query(params: SupportTicketListParams): string {
	const q = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== null && value !== "")
			q.set(key, String(value));
	}
	const s = q.toString();
	return s ? `?${s}` : "";
}

/** PUT a file straight to storage through a write SAS URL issued by the API. */
async function uploadToSas(uploadUrl: string, file: File): Promise<void> {
	const response = await fetch(uploadUrl, {
		method: "PUT",
		headers: {
			"x-ms-blob-type": "BlockBlob",
			"Content-Type": file.type || "application/octet-stream",
		},
		body: file,
	});
	if (!response.ok)
		throw new Error(`Uploading ${file.name} failed (${response.status}).`);
}

export const supportService = {
	list: async (
		params: SupportTicketListParams,
	): Promise<SupportTicketListResult> =>
		(
			await apiFetch<ApiResponse<SupportTicketListResult>>(
				`/support/tickets${query(params)}`,
			)
		).result,

	summary: async (): Promise<SupportSummary> =>
		(await apiFetch<ApiResponse<SupportSummary>>("/support/summary"))
			.result,

	get: async (ticketId: string): Promise<SupportTicketDetail> =>
		(
			await apiFetch<ApiResponse<SupportTicketDetail>>(
				`/support/tickets/${ticketId}`,
			)
		).result,

	create: async (request: CreateStaffTicketRequest) =>
		(
			await apiFetch<
				ApiResponse<{
					ticketId: string;
					displayNumber: string;
					firstMessageId: string;
				}>
			>("/support/tickets", {
				method: "POST",
				body: JSON.stringify(request),
			})
		).result,

	addMessage: async (
		ticketId: string,
		body: string,
		isInternal: boolean,
		attachmentIds: string[],
		setStatus?: SupportTicketStatus,
	) =>
		(
			await apiFetch<
				ApiResponse<{ messageId: string; status: SupportTicketStatus }>
			>(`/support/tickets/${ticketId}/messages`, {
				method: "POST",
				body: JSON.stringify({
					body,
					isInternal,
					attachmentIds,
					setStatus,
				}),
			})
		).result,

	update: async (ticketId: string, request: UpdateSupportTicketRequest) =>
		(
			await apiFetch<ApiResponse<{ ticket: SupportTicketSummary }>>(
				`/support/tickets/${ticketId}`,
				{
					method: "PATCH",
					body: JSON.stringify(request),
				},
			)
		).result,

	uploadFiles: async (ticketId: string, files: File[]): Promise<string[]> => {
		const ids: string[] = [];
		for (const file of files) {
			const slot = (
				await apiFetch<
					ApiResponse<{ attachmentId: string; uploadUrl: string }>
				>(`/support/tickets/${ticketId}/attachments/upload-url`, {
					method: "POST",
					body: JSON.stringify({
						fileName: file.name,
						contentType: file.type,
						fileSize: file.size,
					}),
				})
			).result;
			await uploadToSas(slot.uploadUrl, file);
			ids.push(slot.attachmentId);
		}
		return ids;
	},

	attachmentUrl: async (ticketId: string, attachmentId: string) =>
		(
			await apiFetch<ApiResponse<{ url: string }>>(
				`/support/tickets/${ticketId}/attachments/${attachmentId}`,
			)
		).result,

	assignees: async (): Promise<SupportAssignee[]> =>
		(
			await apiFetch<ApiResponse<{ items: SupportAssignee[] }>>(
				"/support/assignees",
			)
		).result.items,

	getSettings: async (): Promise<SupportSettings> =>
		(await apiFetch<ApiResponse<SupportSettings>>("/support/settings"))
			.result,

	updateSettings: async (
		settings: SupportSettings,
	): Promise<SupportSettings> =>
		(
			await apiFetch<ApiResponse<SupportSettings>>("/support/settings", {
				method: "PUT",
				body: JSON.stringify(settings),
			})
		).result,
};
